import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface CustomInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "type"> {
  inputType?: "text" | "number" | "float" | "time";
  value?: string | number;
  onChange?: (value: string | number | undefined) => void;
  maxDecimals?: number;
  showsuggestion?: boolean;
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    { className, inputType = "text", value: propValue, onChange, maxDecimals = 2, ...props },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState("");
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => {
      if (!isFocused) {
        setInternalValue(
          propValue === undefined ||
            propValue === null ||
            (inputType !== "text" && inputType !== "time" && isNaN(Number(propValue)))
            ? ""
            : String(propValue),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue, inputType]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const currentInputValue = e.target.value;

      if (inputType === "number") {
        if (currentInputValue !== "" && !/^-?\d*$/.test(currentInputValue)) {
          return;
        }
      }

      if (inputType === "float") {
        if (currentInputValue !== "" && !/^-?\d*([.,])?\d*$/.test(currentInputValue)) {
          return;
        }

        if (maxDecimals !== undefined && maxDecimals >= 0) {
          const parts = currentInputValue.split(/[.,]/);
          if (parts[1] && parts[1].length > maxDecimals) {
            return;
          }
        }
      }

      setInternalValue(currentInputValue);

      if (!onChange) return;

      if (currentInputValue === "" || currentInputValue === "-") {
        onChange(undefined);
        return;
      }

      switch (inputType) {
        case "float": {
          const sanitized = currentInputValue.replace(",", ".");
          if (sanitized.endsWith(".")) {
            const num = parseFloat(sanitized);
            onChange(isNaN(num) ? undefined : num);
            return;
          }
          const num = parseFloat(sanitized);
          onChange(isNaN(num) ? undefined : num);
          break;
        }
        case "number": {
          const num = parseInt(currentInputValue, 10);
          onChange(isNaN(num) ? undefined : num);
          break;
        }
        default:
          onChange(currentInputValue);
          break;
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Set cursor to the end of the input
      setTimeout(() => {
        const input = e.target;
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }, 0);
      props.onFocus?.(e);
    };

    if (inputType === "time") {
      return (
        <Input
          {...props}
          ref={ref}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          type={"time"}
          inputMode={"text"}
          autoComplete={props.showsuggestion ? undefined : "off"}
          className={cn(!internalValue && "text-gray-300", className)}
          value={internalValue}
          onChange={handleChange}
        />
      );
    }

    return (
      <Input
        {...props}
        ref={ref}
        onFocus={handleFocus}
        onWheel={(e) => {
          if (inputType === "number" || inputType === "float") {
            e.currentTarget.blur();
          }
        }}
        onBlur={(e) => {
          setIsFocused(false);
          props.onBlur?.(e);
        }}
        type={inputType === "text" ? "text" : "text"}
        inputMode={inputType === "float" ? "decimal" : inputType === "number" ? "numeric" : "text"}
        autoComplete={props.showsuggestion ? undefined : "off"}
        className={cn("", className)}
        value={internalValue}
        onChange={handleChange}
      />
    );
  },
);

CustomInput.displayName = "CustomInput";

export { CustomInput };
